import { useEffect, useRef, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Lightweight "Install" prompt for Casting Calls UK.
 *
 * Chrome / Edge / Android fire `beforeinstallprompt`.
 * iOS Safari does not — users install via Share → Add to Home Screen.
 */

const DISMISS_KEY = 'ccuk_pwa_install_dismissed_at';
const DISMISS_DAYS = 14;

// Keep this outside React so the prompt is not lost if React remounts
// the component during routing/auth/splash changes.
let cachedBeforeInstallPrompt = null;

function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // quota / private mode
  }
}

function isDismissedRecently() {
  try {
    const dismissedAt = parseInt(safeLocalStorageGet(DISMISS_KEY) || '0', 10);
    return !!(
      dismissedAt &&
      Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000
    );
  } catch {
    return true;
  }
}

function safeIsStandalone() {
  try {
    if (typeof window === 'undefined') return false;

    const mm =
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)');

    if (mm && mm.matches) return true;

    if (window.navigator && window.navigator.standalone === true) {
      return true;
    }
  } catch {
    // default to not standalone
  }

  return false;
}

export default function InstallPrompt() {
  const deferredPromptRef = useRef(cachedBeforeInstallPrompt);
  const [visible, setVisible] = useState(() => {
    return !!cachedBeforeInstallPrompt && !safeIsStandalone() && !isDismissedRecently();
  });

  useEffect(() => {
    if (safeIsStandalone()) return undefined;

    const onBip = (e) => {
      try {
        e.preventDefault();

        cachedBeforeInstallPrompt = e;
        deferredPromptRef.current = e;

        if (!isDismissedRecently()) {
          setVisible(true);
        }
      } catch {
        // ignore
      }
    };

    const onInstalled = () => {
      cachedBeforeInstallPrompt = null;
      deferredPromptRef.current = null;
      setVisible(false);
    };

    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);

    // If the event was caught before this component remounted, show it now.
    if (cachedBeforeInstallPrompt && !isDismissedRecently()) {
      setVisible(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    const promptEvent = deferredPromptRef.current || cachedBeforeInstallPrompt;

    if (!promptEvent) {
      setVisible(false);
      return;
    }

    try {
      await promptEvent.prompt();
      await promptEvent.userChoice;
    } catch {
      // user cancelled / browser threw
    } finally {
      cachedBeforeInstallPrompt = null;
      deferredPromptRef.current = null;
      setVisible(false);
    }
  };

  const dismiss = () => {
    safeLocalStorageSet(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible || safeIsStandalone()) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="pwa-install-title"
      data-testid="pwa-install-prompt"
      className="fixed z-[9998] left-3 right-3 sm:left-auto sm:right-4 sm:max-w-[360px] rounded-xl border border-theme bg-theme-secondary shadow-2xl p-3 pr-4"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.25rem)',
      }}
    >
      <button
        onClick={dismiss}
        className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-theme-muted hover:text-theme-text hover:bg-theme/40"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-lg bg-theme-primary/10 flex items-center justify-center">
          <Download className="h-5 w-5 text-theme-primary" />
        </div>

        <div className="min-w-0 flex-1">
          <p id="pwa-install-title" className="text-sm font-semibold text-theme-text leading-tight">
            Install Casting Calls UK
          </p>

          <p className="text-xs text-theme-muted mt-1 leading-snug">
            Add to your home screen for faster access and a full-screen experience.
          </p>

          <div className="mt-3 flex gap-2">
            <button
              onClick={install}
              className="px-4 py-2 text-sm font-medium rounded-md bg-theme-primary text-white hover:opacity-90 transition-opacity min-h-[40px]"
              data-testid="pwa-install-accept"
            >
              Install app
            </button>

            <button
              onClick={dismiss}
              className="px-3 py-2 text-sm font-medium rounded-md text-theme-muted hover:text-theme-text min-h-[40px]"
              data-testid="pwa-install-later"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}